/**
 * EntityPopup — Holding company / BV detail popup (Phase A1).
 *
 * Renders a Belgian-PCMN balance sheet for an Entity (Brutus & Co BV first):
 *   - Assets / Liabilities side-by-side
 *   - Equity composition with click-through to tax-cost-of-cash-out modal
 *   - View toggle (Accounting / Economic / P&L*)
 *   - Date picker (snapshots only)
 *   - Click-through detail modals for Maxus, Monarch, R/C, ING cash, Liquidatiereserves
 *   - Inline editable values (saves a snapshot on every change)
 *
 * Loaded from InvestmentList.js when the user clicks the "Brutus & Co BV" card.
 */

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { apiGet, apiPut } from '../../utils/api';
import './EntityPopup.css';
import SeeFilesButton from './SeeFilesButton';
import { getDriveFolderUrl } from './driveFolders';

// ─── Formatters ───────────────────────────────────────────────────────────────
const fmtEur = (v, decimals = 0) => {
  if (v == null || isNaN(v)) return '—';
  const sign = v < 0 ? '-' : '';
  return `${sign}€${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: decimals, minimumFractionDigits: decimals })}`;
};
const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// ─── Belgian RV cost calculator (static rates) ────────────────────────────────
const computeTaxCost = (positions) => {
  const reserves = positions.filter(p => p.category === 'equity_reserve');
  const today = new Date();
  return reserves.map(r => {
    const value = r.values?.accounting?.value_eur || 0;
    const eligibleFromIso = r.detail?.eligible_5pct_from || '';
    const eligibleFrom = eligibleFromIso ? new Date(`${eligibleFromIso}-01`) : null;
    const eligibleNow = eligibleFrom && eligibleFrom <= today;

    const cost_now_pct  = eligibleNow ? 5 : 30;     // 5% if past holding period, else 30%
    const cost_wait_pct = 5;
    const cost_now_eur  = value * cost_now_pct / 100;
    const cost_wait_eur = value * cost_wait_pct / 100;
    const savings_if_wait = eligibleNow ? 0 : cost_now_eur - cost_wait_eur;

    return {
      label: r.label,
      value,
      eligibleFromIso,
      eligibleNow,
      cost_now_pct,
      cost_now_eur,
      cost_wait_eur,
      savings_if_wait,
    };
  });
};

// ─── Display helpers ──────────────────────────────────────────────────────────
const groupBy = (positions, keyFn) => {
  const out = {};
  for (const p of positions) {
    const k = keyFn(p) || '';
    if (!out[k]) out[k] = [];
    out[k].push(p);
  }
  return out;
};

const isClickable = (p) =>
  ['investment_fund', 'structured_product', 'rc_loan', 'cash_account', 'equity_reserve', 'deposit_fund'].includes(p.category);

// ─── Returns computation for tradeable positions (ETFs, funds) ────────────────
// Uses detail.nav_reports as the price-history series; falls back to "—" when
// the timeframe boundary predates the earliest available data point. All-time
// is always computed against cost_basis_eur (not the earliest nav_report).
const computeReturns = (p) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  const dayOfWeek = today.getDay() === 0 ? 7 : today.getDay(); // Mon=1..Sun=7
  const monday = new Date(today); monday.setDate(today.getDate() - (dayOfWeek - 1));
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const yearStart  = new Date(today.getFullYear(), 0, 1);

  const currentValue = p.values?.economic?.value_eur ?? p.values?.accounting?.value_eur ?? 0;
  const costBasis    = p.detail?.cost_basis_eur ?? p.values?.accounting?.value_eur ?? 0;

  const reports = (p.detail?.nav_reports || [])
    .map(r => ({ date: new Date(r.date), value: r.value_eur }))
    .filter(r => !isNaN(r.date) && r.value != null)
    .sort((a, b) => a.date - b.date);

  // Latest report's value <= the cutoff date
  const valueAtOrBefore = (cutoff) => {
    const matches = reports.filter(r => r.date <= cutoff);
    return matches.length ? matches[matches.length - 1].value : null;
  };
  // For "Day" we want the most recent point STRICTLY before today
  const lastBeforeToday = () => {
    const matches = reports.filter(r => r.date < today);
    return matches.length ? matches[matches.length - 1].value : null;
  };

  const ret = (ref) => {
    if (ref == null || ref === 0) return null;
    const abs = currentValue - ref;
    return { abs, pct: abs / ref * 100, ref };
  };

  return {
    current:    currentValue,
    costBasis,
    day:        ret(lastBeforeToday()),
    wtd:        ret(valueAtOrBefore(new Date(monday.getTime() - 1))),    // last close before Monday open
    mtd:        ret(valueAtOrBefore(new Date(monthStart.getTime() - 1))),
    ytd:        ret(valueAtOrBefore(new Date(yearStart.getTime() - 1))),
    allTime:    ret(costBasis),
    earliestNav: reports[0]?.date || null,
    latestNav:   reports[reports.length - 1]?.date || null,
  };
};

// ─── DetailModal — wrapper for click-through detail ───────────────────────────
function DetailModal({ title, onClose, children }) {
  return (
    <div className="ent-detail-overlay" onClick={onClose}>
      <div className="ent-detail-modal" onClick={e => e.stopPropagation()}>
        <h3>
          <span>{title}</span>
          <button className="ent-close-btn" onClick={onClose}>×</button>
        </h3>
        {children}
      </div>
    </div>
  );
}

// ─── Main popup ───────────────────────────────────────────────────────────────
function EntityPopup({ slug, onClose }) {
  const [view, setView]               = useState('economic');  // default to Economic view
  const [snapshotDate, setSnapDate]   = useState(null); // null = live
  const [snapshots, setSnapshots]     = useState([]);
  const [meta, setMeta]               = useState(null);
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [openDetail, setOpenDetail]   = useState(null); // 'maxus-i' | 'maxus-ii' | 'monarch' | 'cash' | 'rc' | 'liq' | 'tax'
  const [editingCode, setEditing]     = useState(null);
  const [draftValue, setDraft]        = useState('');
  const [openMonarchProduct, setOpenMonarchProduct] = useState(null); // product id when monarch modal expands one

  // ─── Fetch ────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ view });
      if (snapshotDate) params.set('date', snapshotDate);
      const r = await apiGet(`/api/entities/${slug}/view?${params.toString()}`);
      if (!r.success) throw new Error(r.message || 'Failed to load entity');
      setMeta(r.meta);
      setData(r.view);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [slug, view, snapshotDate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    apiGet(`/api/entities/${slug}/snapshots`).then(r => {
      if (r.success) setSnapshots(r.snapshots || []);
    }).catch(() => {});
  }, [slug]);

  // ─── Save edit ────────────────────────────────────────────────────────────
  const saveEdit = async (code) => {
    const num = parseFloat(draftValue.replace(/[^0-9.\-]/g, ''));
    if (isNaN(num)) { setEditing(null); return; }
    try {
      await apiPut(`/api/entities/${slug}/positions/${code}`, { value_eur: num, view });
      setEditing(null);
      setDraft('');
      await load();
    } catch (e) {
      alert('Save failed: ' + e.message);
    }
  };

  // ─── Group positions ──────────────────────────────────────────────────────
  const grouped = useMemo(() => {
    if (!data?.positions) return { assets: {}, liabilities: {}, equity: [] };
    const assets      = data.positions.filter(p => p.ledger_side === 'asset');
    const liabilities = data.positions.filter(p => p.ledger_side === 'liability');
    const equity      = data.positions.filter(p => p.ledger_side === 'equity');
    return {
      assets:      groupBy(assets,      p => p.group),
      liabilities: groupBy(liabilities, p => p.group),
      equity,
    };
  }, [data]);

  // ─── Render rows ──────────────────────────────────────────────────────────
  const renderRow = (p) => {
    const valueObj = view === 'economic' ? p.values?.economic : p.values?.accounting;
    const value = (valueObj?.value_eur != null) ? valueObj.value_eur : (p.values?.accounting?.value_eur || 0);
    const clickable = isClickable(p);

    let detailKey = null;
    if (p.code === '284000') detailKey = 'maxus-i';
    else if (p.code === '284010') detailKey = 'maxus-ii';
    else if (p.code === '510100') detailKey = 'monarch';
    else if (p.code === '550020') detailKey = 'cash';
    else if (p.code === '510300') detailKey = 'ing-dbi';
    else if (p.code === '510310') detailKey = 'vaneck';
    else if (p.code === '510320') detailKey = 'micron';
    else if (p.code === '416120' || p.code === '416100') detailKey = 'rc:' + p.code;

    const isEditing = editingCode === p.code;
    const showLive = p.code === '550020' && data?.positions?.find(x => x.code === '550020')?.values?.accounting?.valuation_date;

    return (
      <div
        key={p.code}
        className={'ent-row' + (clickable ? ' ent-clickable' : '')}
        onClick={(e) => {
          if (e.target.tagName === 'INPUT') return;
          if (detailKey) setOpenDetail(detailKey);
        }}
      >
        <span className="ent-row-label">
          {p.label}
          {showLive && <span className="ent-live-badge">live</span>}
        </span>
        {isEditing ? (
          <input
            className="ent-edit-input"
            type="text"
            value={draftValue}
            autoFocus
            onClick={e => e.stopPropagation()}
            onChange={e => setDraft(e.target.value)}
            onBlur={() => saveEdit(p.code)}
            onKeyDown={e => { if (e.key === 'Enter') saveEdit(p.code); if (e.key === 'Escape') setEditing(null); }}
            style={{ maxWidth: 130 }}
          />
        ) : (
          <span
            className="ent-row-value"
            onDoubleClick={(e) => {
              e.stopPropagation();
              setEditing(p.code);
              setDraft(value.toString());
            }}
            title="Double-click to edit"
          >
            {fmtEur(value)}
          </span>
        )}
        {clickable && detailKey && <span className="ent-row-chevron">▸</span>}
      </div>
    );
  };

  const renderColumn = (groups, sideTotal, label) => (
    <div className="ent-bs-col">
      <h3><span>{label}</span><span>{fmtEur(sideTotal)}</span></h3>
      {Object.entries(groups).map(([groupName, items]) => (
        <div key={groupName}>
          {groupName && <div className="ent-group-label">{groupName}</div>}
          {items.map(renderRow)}
        </div>
      ))}
    </div>
  );

  // ─── Detail content per modal type ────────────────────────────────────────
  const detailContent = () => {
    if (!openDetail || !data) return null;

    if (openDetail.startsWith('maxus-')) {
      const code = openDetail === 'maxus-i' ? '284000' : '284010';
      const p = data.positions.find(x => x.code === code);
      if (!p) return null;
      const d = p.detail || {};
      return (
        <DetailModal title={p.label} onClose={() => setOpenDetail(null)}>
          <table className="ent-detail-table">
            <tbody>
              <tr><th>Commitment</th><td className="num">{fmtEur(d.commitment_eur)}</td></tr>
              <tr><th>Called</th><td className="num">{fmtEur(d.called_eur)}</td></tr>
              <tr><th>Unfunded Commitment</th><td className="num">{fmtEur(d.uncalled_eur)}</td></tr>
              <tr><th>Distributed</th><td className="num">{fmtEur(d.distributed_eur)}</td></tr>
              <tr><th>Accounting value</th><td className="num">{fmtEur(p.values?.accounting?.value_eur)}</td></tr>
              <tr><th>Economic value</th><td className="num">{p.values?.economic?.value_eur ? fmtEur(p.values.economic.value_eur) : <em>not set — please upload latest NAV</em>}</td></tr>
            </tbody>
          </table>
          {(d.capital_calls?.length > 0) && (<>
            <h4 style={{ margin: '14px 0 6px' }}>Capital calls</h4>
            <table className="ent-detail-table">
              <thead><tr><th>Date</th><th className="num">Joint</th><th className="num">Share</th><th className="num">Brutus</th></tr></thead>
              <tbody>{d.capital_calls.map((c, i) => {
                const brutus = c.brutus_eur ?? c.amount_eur ?? 0;
                const joint  = c.joint_eur  ?? c.amount_eur ?? 0;
                const share  = c.share_pct ?? (joint ? Math.round(brutus/joint*10000)/100 : null);
                return (
                  <tr key={i}>
                    <td>{fmtDate(c.date)}</td>
                    <td className="num">{fmtEur(joint)}</td>
                    <td className="num">{share != null ? share.toFixed(2)+'%' : '—'}</td>
                    <td className="num">{fmtEur(brutus)}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          </>)}
          {(d.distributions?.length > 0) && (<>
            <h4 style={{ margin: '14px 0 6px' }}>Distributions</h4>
            <table className="ent-detail-table">
              <thead><tr><th>Date</th><th className="num">Joint</th><th className="num">Share</th><th className="num">Brutus</th></tr></thead>
              <tbody>{d.distributions.map((c, i) => {
                const brutus = c.brutus_eur ?? c.amount_eur ?? 0;
                const joint  = c.joint_eur  ?? c.amount_eur ?? 0;
                const share  = c.share_pct ?? (joint ? Math.round(brutus/joint*10000)/100 : null);
                return (
                  <tr key={i}>
                    <td>{fmtDate(c.date)}</td>
                    <td className="num">{fmtEur(joint)}</td>
                    <td className="num">{share != null ? share.toFixed(2)+'%' : '—'}</td>
                    <td className="num">{fmtEur(brutus)}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          </>)}
        </DetailModal>
      );
    }

    if (openDetail === 'ing-dbi' || openDetail === 'vaneck' || openDetail === 'micron') {
      const TRADEABLE = { 'ing-dbi': '510300', vaneck: '510310', micron: '510320' };
      const code = TRADEABLE[openDetail];
      const p = data.positions.find(x => x.code === code);
      if (!p) return null;
      const d = p.detail || {};
      const r = computeReturns(p);
      const tranches = d.tranches || [];
      const txGrossSum = (d.transactions || []).reduce((s, t) => s + (t.gross_eur || 0), 0);
      const txCosts = d.cost_basis_eur != null && txGrossSum > 0
        ? +(d.cost_basis_eur - txGrossSum).toFixed(2)
        : null;
      const avgCost = d.units ? d.cost_basis_eur / d.units : null;
      // Non-EUR listings (Micron MU:NSQ in USD): the cron stores the EUR-converted price in
      // current_price_eur and, since Oct-2026, the raw quote + ECB FX in current_price_quote / current_fx_rate.
      const qCcy = d.quote_currency && d.quote_currency !== 'EUR' ? d.quote_currency : null;
      const qSym = qCcy === 'USD' ? '$' : qCcy === 'GBP' ? '£' : (qCcy ? qCcy + ' ' : '');
      const cronLabel = code === '510300' ? 'FT.com NAV, daily 19:25 LDN cron'
        : code === '510310' ? 'Xetra live 19:00 + official close 23:30 LDN cron'
        : 'FT.com MU:NSQ USD close × ECB FX, 22:10 LDN cron Mon–Fri';
      const unrealised = r.current != null && d.cost_basis_eur != null
        ? { abs: r.current - d.cost_basis_eur, pct: (r.current - d.cost_basis_eur) / d.cost_basis_eur * 100 }
        : null;

      const retCell = (ret) => {
        if (!ret) return <><td className="num">—</td><td className="num">—</td></>;
        const color = ret.abs >= 0 ? '#16a34a' : '#dc2626';
        const sign = ret.abs >= 0 ? '+' : '';
        return (
          <>
            <td className="num" style={{ color }}>{sign}{fmtEur(ret.abs, 2)}</td>
            <td className="num" style={{ color }}>{sign}{ret.pct.toFixed(2)}%</td>
          </>
        );
      };

      return (
        <DetailModal
          title={`${p.label}${d.isin ? ' — ' + d.isin : ''}`}
          onClose={() => setOpenDetail(null)}
        >
          <table className="ent-detail-table">
            <tbody>
              <tr><th>Shares owned</th><td className="num">{d.units != null ? d.units.toLocaleString(undefined, { maximumFractionDigits: 4 }) : '—'}</td></tr>
              <tr><th>Cost basis</th><td className="num">{fmtEur(d.cost_basis_eur, 2)}</td></tr>
              <tr><th>Avg cost / share</th><td className="num">{avgCost != null ? '€' + avgCost.toFixed(2) : '—'}</td></tr>
              <tr><th>Transaction costs</th><td className="num">{txCosts != null ? fmtEur(txCosts, 2) : <em style={{ color: '#6b7280' }}>n/a</em>}</td></tr>
              <tr><th>Current price</th><td className="num">{d.current_price_eur != null ? '€' + d.current_price_eur.toFixed(2) : '—'}{qCcy && d.current_price_quote != null && (<span style={{ color: '#6b7280', fontWeight: 400 }}> ({qSym}{d.current_price_quote.toFixed(2)}{d.current_fx_rate ? ' @ ' + qCcy + '/EUR ' + d.current_fx_rate.toFixed(4) : ''})</span>)}</td></tr>
              <tr><th>Current value</th><td className="num"><strong>{fmtEur(r.current, 2)}</strong></td></tr>
              {unrealised && (
                <tr>
                  <th>Unrealised P&amp;L</th>
                  <td className="num" style={{ color: unrealised.abs >= 0 ? '#16a34a' : '#dc2626' }}>
                    <strong>{(unrealised.abs >= 0 ? '+' : '') + fmtEur(unrealised.abs, 2)}</strong>
                    {' '}({(unrealised.pct >= 0 ? '+' : '') + unrealised.pct.toFixed(2)}%)
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {tranches.length > 0 && (<>
            <h4 style={{ margin: '14px 0 6px' }}>By tranche</h4>
            <table className="ent-detail-table">
              <thead><tr><th>#</th><th>Date</th><th className="num">Units</th><th className="num">Avg price</th><th className="num">Gross</th><th className="num">Now</th><th className="num">Return</th></tr></thead>
              <tbody>{tranches.map((t, i) => {
                const trNow  = (d.current_price_eur || 0) * (t.units || 0);
                const trCost = t.gross_eur || 0;
                const trRet  = trCost ? (trNow - trCost) / trCost * 100 : null;
                const color = trRet != null && trRet >= 0 ? '#16a34a' : '#dc2626';
                return (
                  <tr key={i}>
                    <td>T{t.tranche}</td>
                    <td>{fmtDate(t.date)}</td>
                    <td className="num">{(t.units || 0).toLocaleString()}</td>
                    <td className="num">{t.avg_price_eur != null ? '€' + t.avg_price_eur.toFixed(2) : t.avg_price_usd != null ? '$' + t.avg_price_usd.toFixed(2) : '—'}</td>
                    <td className="num">{fmtEur(trCost)}</td>
                    <td className="num">{fmtEur(trNow)}</td>
                    <td className="num" style={{ color }}>{trRet != null ? (trRet >= 0 ? '+' : '') + trRet.toFixed(2) + '%' : '—'}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          </>)}

          <h4 style={{ margin: '14px 0 6px' }}>Returns</h4>
          <table className="ent-detail-table">
            <thead><tr><th></th><th className="num">Absolute</th><th className="num">%</th></tr></thead>
            <tbody>
              <tr><th>Day</th>      {retCell(r.day)}</tr>
              <tr><th>WTD</th>      {retCell(r.wtd)}</tr>
              <tr><th>MTD</th>      {retCell(r.mtd)}</tr>
              <tr><th>YTD</th>      {retCell(r.ytd)}</tr>
              <tr><th>All-time</th> {retCell(r.allTime)}</tr>
            </tbody>
          </table>
          <p style={{ fontSize: 11, color: '#6b7280', marginTop: 8, lineHeight: 1.4 }}>
            Day/WTD/MTD/YTD computed from <code>detail.nav_reports</code> ({cronLabel}).
            {r.earliestNav && r.latestNav && (
              <> Series spans {fmtDate(r.earliestNav)} → {fmtDate(r.latestNav)}.</>
            )}
            {' '}Where the timeframe boundary predates the series, the cell shows "—". All-time uses cost basis.{qCcy && <> Returns are in EUR and therefore include {qCcy}/EUR moves.</>}
          </p>
        </DetailModal>
      );
    }

    if (openDetail === 'monarch') {
      const p = data.positions.find(x => x.code === '510100');
      if (!p) return null;
      const d = p.detail || {};
      const products = d.structured_products || [];
      const share = (d.brutus_share_pct ?? 50) / 100;

      // Aggregate values
      const totalAfkoopJoint = products.reduce((s, x) => s + (x.monarch_afkoopwaarde_joint_eur || 0), 0)
                            || d.joint_total_afkoopwaarde_eur || 0;
      const totalFvJoint     = products.reduce((s, x) => s + (x.fair_value?.fv_joint_eur || 0), 0);
      const totalCostJoint   = products.reduce((s, x) => s + (x.cost_paid_joint_eur || 0), 0);

      const positive = '#16a34a', negative = '#dc2626';
      const pctColor = v => v == null ? '#6b7280' : (v >= 0 ? positive : negative);
      const fmtUnits = v => v == null ? '—' : v.toLocaleString(undefined, { maximumFractionDigits: 4 });
      const fmtPct = (v, dec = 1) => v == null ? '—' : `${v >= 0 ? '+' : ''}${(v * 100).toFixed(dec)}%`;

      // Per-product row
      const renderProductTile = (prod) => {
        const isOpen = openMonarchProduct === prod.id;
        const isAutocall = prod.payoff_type === 'autocall_degressive';
        const autocallIdx = (prod.indices || []).find(i => i.role === 'autocall_trigger') || (prod.indices || [])[0];
        const upIdx  = (prod.indices || []).find(i => i.role === 'upside');
        const dipIdx = (prod.indices || []).find(i => i.role === 'dip' || i.role === 'protection');
        const fvJoint = prod.fair_value?.fv_joint_eur;
        const fvShare = prod.fair_value?.fv_share_eur;
        const afkoop  = prod.monarch_afkoopwaarde_joint_eur;
        const cost    = prod.cost_paid_joint_eur;

        // Headline stats for the collapsed tile header
        let headline = null;
        if (isAutocall && autocallIdx) {
          const spotVsStrike = (autocallIdx.current_spot / autocallIdx.strike) - 1;
          const nextObs = prod.autocall_schedule?.observations?.[0];
          const nextThresholdLevel = autocallIdx.strike * (nextObs?.threshold_pct ?? 1);
          const buffer = (autocallIdx.current_spot - nextThresholdLevel) / nextThresholdLevel;
          const yearProb = prod.last_pricing_details?.per_year?.[0]?.probability;
          headline = (
            <div style={{ fontSize: 12, color: '#374151', marginTop: 4 }}>
              Spot <b>{autocallIdx.current_spot?.toFixed(2)}</b> vs strike {autocallIdx.strike} <span style={{ color: pctColor(spotVsStrike) }}>({fmtPct(spotVsStrike)})</span>
              {nextObs && <> · Next obs <b>{fmtDate(nextObs.date)}</b> @ {(nextObs.threshold_pct*100).toFixed(0)}% (= {nextThresholdLevel.toFixed(2)}, buffer <span style={{ color: pctColor(buffer) }}>{fmtPct(buffer)}</span>)</>}
              {yearProb != null && <> · <b style={{ color: '#16a34a' }}>{(yearProb*100).toFixed(1)}% autocall prob</b></>}
            </div>
          );
        } else if (upIdx && dipIdx) {
          const upPerf  = (upIdx.current_spot / upIdx.strike) - 1;
          const dipPerf = (dipIdx.current_spot / dipIdx.strike) - 1;
          const dipBarrier = prod.payoff_params?.dip_barrier_pct ?? 0.5;
          const dipBufPct = (dipPerf - (-dipBarrier));
          const pWithCoupon = prod.last_pricing_details?.probability_partial_coupon != null
            ? prod.last_pricing_details.probability_partial_coupon + (prod.last_pricing_details.probability_capped || 0)
            : null;
          headline = (
            <div style={{ fontSize: 12, color: '#374151', marginTop: 4 }}>
              Upside {upIdx.ticker} <span style={{ color: pctColor(upPerf) }}>{fmtPct(upPerf)}</span>
              {' · '}DIP {dipIdx.ticker} <span style={{ color: pctColor(dipPerf) }}>{fmtPct(dipPerf)}</span> (buffer <span style={{ color: pctColor(dipBufPct) }}>{fmtPct(dipBufPct, 0)}</span> above {(dipBarrier*100).toFixed(0)}% trigger)
              {pWithCoupon != null && <> · <b>{(pWithCoupon*100).toFixed(0)}% coupon prob</b></>}
            </div>
          );
        }

        const tileBg = isOpen ? '#FAF8F6' : '#FFFFFF';
        return (
          <div key={prod.id} style={{ border: '1px solid #e5e7eb', borderRadius: 6, marginBottom: 10, background: tileBg }}>
            <div
              onClick={() => setOpenMonarchProduct(isOpen ? null : prod.id)}
              style={{ padding: '10px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                    background: isAutocall ? '#16a34a' : '#3b82f6', color: 'white',
                  }}>
                    {isAutocall ? 'AUTOCALLABLE' : '10Y MATURITY'}
                  </span>
                  <strong>{prod.label}</strong>
                  <span style={{ fontSize: 11, color: '#6b7280' }}>· {prod.isin}</span>
                </div>
                {headline}
              </div>
              <div style={{ textAlign: 'right', minWidth: 160 }}>
                <div style={{ fontSize: 11, color: '#6b7280' }}>Fair value (Brutus 50%)</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#0f2840' }}>{fvShare ? fmtEur(fvShare, 0) : '—'}</div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>{isOpen ? '▾ collapse' : '▸ expand'}</div>
              </div>
            </div>

            {isOpen && (
              <div style={{ padding: '0 14px 14px 14px', borderTop: '1px solid #e5e7eb' }}>
                {/* Structure + Index blocks */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 12 }}>
                  <div>
                    <h4 style={{ margin: '0 0 6px 0', fontSize: 12, color: '#1F4E79', textTransform: 'uppercase' }}>Structure</h4>
                    <table className="ent-detail-table" style={{ fontSize: 12 }}>
                      <tbody>
                        <tr><th>ISIN</th><td>{prod.isin}</td></tr>
                        <tr><th>Issuer</th><td>{prod.issuer}</td></tr>
                        <tr><th>Guarantor</th><td>{prod.guarantor}</td></tr>
                        <tr><th>Currency</th><td>{prod.currency}</td></tr>
                        <tr><th>Issue date</th><td>{fmtDate(prod.issue_date)}</td></tr>
                        <tr><th>Final maturity</th><td>{fmtDate(prod.maturity_date)}</td></tr>
                        <tr><th>Joint nominal</th><td>{fmtEur(prod.nominal_joint_eur)}</td></tr>
                        <tr><th>Brutus share</th><td>50% = {fmtEur(prod.nominal_joint_eur * share)}</td></tr>
                      </tbody>
                    </table>
                  </div>
                  <div>
                    <h4 style={{ margin: '0 0 6px 0', fontSize: 12, color: '#1F4E79', textTransform: 'uppercase' }}>Underlying indices</h4>
                    {(prod.indices || []).map(idx => {
                      const perf = (idx.current_spot / idx.strike) - 1;
                      return (
                        <div key={idx.ticker} style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase' }}>
                            {idx.role === 'autocall_trigger' ? 'Autocall trigger' : idx.role === 'upside' ? 'Upside participation' : 'DIP / capital protection'}
                          </div>
                          <div style={{ fontSize: 12 }}><b>{idx.ticker}</b> — {idx.name}</div>
                          <table className="ent-detail-table" style={{ fontSize: 12, marginTop: 4 }}>
                            <tbody>
                              <tr><th>Strike</th><td>{idx.strike?.toLocaleString()}</td></tr>
                              <tr>
                                <th>Spot today</th>
                                <td>
                                  <b>{idx.current_spot?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</b>{' '}
                                  <span style={{ color: pctColor(perf) }}>({fmtPct(perf)})</span>
                                  {idx.last_updated && <span style={{ color: '#9ca3af', fontSize: 10 }}> · {fmtDate(idx.last_updated)}</span>}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Autocall schedule OR Payoff scenarios */}
                {isAutocall && prod.autocall_schedule && (
                  <>
                    <h4 style={{ margin: '14px 0 6px 0', fontSize: 12, color: '#1F4E79', textTransform: 'uppercase' }}>Autocall schedule</h4>
                    <table className="ent-detail-table" style={{ fontSize: 12 }}>
                      <thead>
                        <tr><th>Date</th><th className="num">Threshold %</th><th className="num">Index level</th><th className="num">Payout</th><th className="num">Probability</th></tr>
                      </thead>
                      <tbody>
                        {prod.autocall_schedule.observations.map((o, i) => {
                          const thr = autocallIdx?.strike * o.threshold_pct;
                          const prob = prod.last_pricing_details?.per_year?.[i]?.probability;
                          const isNext = i === 0;
                          return (
                            <tr key={i} style={isNext ? { background: '#E8F5E9', fontWeight: 600 } : {}}>
                              <td>{fmtDate(o.date)}{isNext && ' ◄ next'}</td>
                              <td className="num">{(o.threshold_pct*100).toFixed(0)}%</td>
                              <td className="num">{thr?.toFixed(2)}</td>
                              <td className="num">{(o.payout_pct*100).toFixed(0)}%</td>
                              <td className="num" style={{ color: pctColor(prob - 0.001) }}>{prob != null ? `${(prob*100).toFixed(1)}%` : '—'}</td>
                            </tr>
                          );
                        })}
                        <tr>
                          <td>{fmtDate(prod.autocall_schedule.final_maturity.date)}</td>
                          <td className="num">final</td>
                          <td className="num">—</td>
                          <td className="num">scenarios</td>
                          <td className="num">{prod.last_pricing_details?.final_probability != null ? `${(prod.last_pricing_details.final_probability*100).toFixed(1)}%` : '—'}</td>
                        </tr>
                      </tbody>
                    </table>
                    {prod.last_pricing_details?.cumulative_autocall_prob != null && (
                      <p style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                        Cumulative autocall probability: <b>{(prod.last_pricing_details.cumulative_autocall_prob*100).toFixed(1)}%</b>
                      </p>
                    )}
                  </>
                )}

                {!isAutocall && prod.payoff_params && (
                  <>
                    <h4 style={{ margin: '14px 0 6px 0', fontSize: 12, color: '#1F4E79', textTransform: 'uppercase' }}>Payoff scenarios at {fmtDate(prod.maturity_date)}</h4>
                    <table className="ent-detail-table" style={{ fontSize: 12 }}>
                      <thead><tr><th>Scenario</th><th>Trigger</th><th>Payout</th><th className="num">Probability</th></tr></thead>
                      <tbody>
                        <tr>
                          <td>Capital loss</td>
                          <td>DIP {dipIdx.ticker} &lt; −{(prod.payoff_params.dip_barrier_pct*100).toFixed(0)}% from strike</td>
                          <td>= DIP performance</td>
                          <td className="num" style={{ color: pctColor(-prod.last_pricing_details?.probability_capital_loss) }}>
                            {prod.last_pricing_details?.probability_capital_loss != null ? `${(prod.last_pricing_details.probability_capital_loss*100).toFixed(1)}%` : '—'}
                          </td>
                        </tr>
                        <tr>
                          <td>No coupon</td>
                          <td>Upside {upIdx.ticker} ≤ 0%, DIP OK</td>
                          <td>100% (capital back)</td>
                          <td className="num">{prod.last_pricing_details?.probability_protected_no_coupon != null ? `${(prod.last_pricing_details.probability_protected_no_coupon*100).toFixed(1)}%` : '—'}</td>
                        </tr>
                        <tr style={{ background: '#E8F5E9' }}>
                          <td><b>With coupon</b></td>
                          <td>Upside &gt; 0%, DIP OK</td>
                          <td>100% + {(prod.payoff_params.participation*100).toFixed(0)}% × max(0, perf){prod.payoff_params.cap_pct ? `, cap ${(prod.payoff_params.cap_pct*100).toFixed(1)}%` : ''}</td>
                          <td className="num" style={{ color: '#16a34a' }}>
                            <b>{(((prod.last_pricing_details?.probability_partial_coupon || 0) + (prod.last_pricing_details?.probability_capped || 0))*100).toFixed(1)}%</b>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </>
                )}

                {/* Valuation */}
                <h4 style={{ margin: '14px 0 6px 0', fontSize: 12, color: '#1F4E79', textTransform: 'uppercase' }}>Valuation</h4>
                <table className="ent-detail-table" style={{ fontSize: 12 }}>
                  <thead><tr><th></th><th className="num">Joint</th><th className="num">Brutus 50%</th></tr></thead>
                  <tbody>
                    <tr><th>Cost basis</th><td className="num">{fmtEur(cost)}</td><td className="num">{fmtEur(cost * share)}</td></tr>
                    <tr><th>Monarch afkoopwaarde</th><td className="num">{fmtEur(afkoop)}</td><td className="num">{fmtEur(afkoop * share)}</td></tr>
                    <tr style={{ background: '#FAF8F6' }}><th>MC fair value</th><td className="num"><b>{fmtEur(fvJoint, 0)}</b></td><td className="num"><b>{fmtEur(fvShare, 0)}</b></td></tr>
                    {fvJoint != null && cost && (
                      <tr><th>P&L vs cost (FV)</th>
                        <td className="num" style={{ color: pctColor(fvJoint - cost) }}>{fmtEur(fvJoint - cost, 0)} ({fmtPct((fvJoint-cost)/cost)})</td>
                        <td className="num" style={{ color: pctColor(fvJoint - cost) }}>{fmtEur((fvJoint - cost) * share, 0)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {prod.pricing_inputs && (
                  <p style={{ fontSize: 10, color: '#9ca3af', marginTop: 8 }}>
                    Pricer inputs: {Object.entries(prod.pricing_inputs).map(([k,v]) => `${k}=${v}`).join(', ')}
                  </p>
                )}
              </div>
            )}
          </div>
        );
      };

      return (
        <DetailModal
          title={`Monarch Assurance — MIP ${d.mip_no || ''}`}
          onClose={() => { setOpenDetail(null); setOpenMonarchProduct(null); }}
        >
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
            Tak 23 wrapper · joint with {d.joint_with || 'Zandberg BV'} · Brutus share {(share*100).toFixed(0)}%
            {d.last_pricing_run?.as_of && <> · last pricer run {fmtDate(d.last_pricing_run.as_of)}</>}
          </div>

          {/* Top summary card */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
            {[
              { label: 'Afkoopwaarde (joint)',   value: totalAfkoopJoint, sub: 'Monarch surrender quote' },
              { label: 'Fair value (joint, MC)', value: totalFvJoint || null, sub: 'Our MC pricer', highlight: true },
              { label: 'Cost basis (joint)',     value: totalCostJoint, sub: 'paid at inception' },
            ].map((s, i) => (
              <div key={i} style={{
                padding: 12, borderRadius: 6,
                background: s.highlight ? '#0F2840' : '#F3F4F6',
                color:      s.highlight ? 'white' : '#111827',
              }}>
                <div style={{ fontSize: 10, opacity: 0.7, textTransform: 'uppercase' }}>{s.label}</div>
                <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }}>{s.value ? fmtEur(s.value, 0) : '—'}</div>
                <div style={{ fontSize: 10, opacity: 0.7, marginTop: 2 }}>{s.sub}</div>
              </div>
            ))}
          </div>

          {/* Per-product tiles */}
          {products.length === 0 && (
            <p style={{ color: '#6b7280', fontSize: 13 }}>No structured_products data on this position yet. Run the Monarch pricer to populate.</p>
          )}
          {products.map(renderProductTile)}
        </DetailModal>
      );
    }

    if (openDetail === 'cash') {
      const p = data.positions.find(x => x.code === '550020');
      const txs = p?.detail?.transactions_recent || [];
      return (
        <DetailModal title={`ING ${p?.detail?.iban || ''} — recent transactions`} onClose={() => setOpenDetail(null)}>
          <p style={{ fontSize: 13 }}>Current balance: <strong>{fmtEur(p?.values?.accounting?.value_eur, 2)}</strong> as of {fmtDate(p?.values?.accounting?.valuation_date)}</p>
          <table className="ent-detail-table">
            <thead><tr><th>Date</th><th className="num">Amount</th><th>Description</th><th>Class</th></tr></thead>
            <tbody>{txs.map((t, i) => (
              <tr key={i}>
                <td>{fmtDate(t.booking_date)}</td>
                <td className="num">{fmtEur(t.amount_eur, 2)}</td>
                <td style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.description}>{t.description}</td>
                <td><code style={{ fontSize: 11 }}>{t.classification}</code></td>
              </tr>
            ))}</tbody>
          </table>
        </DetailModal>
      );
    }

    if (openDetail.startsWith('rc:')) {
      const code = openDetail.split(':')[1];
      const p = data.positions.find(x => x.code === code);
      if (!p) return null;
      return (
        <DetailModal title={p.label} onClose={() => setOpenDetail(null)}>
          <p style={{ fontSize: 13 }}>{p.detail?.note || 'Intra-group receivable.'}</p>
          <table className="ent-detail-table">
            <tbody>
              <tr><th>Outstanding</th><td className="num">{fmtEur(p.values?.accounting?.value_eur)}</td></tr>
              <tr><th>Type</th><td>R/C — current account loan</td></tr>
              <tr><th>Term</th><td>{code === '416120' ? 'long-term, rolling' : 'short-term'}</td></tr>
            </tbody>
          </table>
        </DetailModal>
      );
    }

    if (openDetail === 'liq') {
      const reserves = data.positions.filter(p => p.category === 'equity_reserve');
      return (
        <DetailModal title="Liquidatiereserves — by boekjaar" onClose={() => setOpenDetail(null)}>
          <table className="ent-detail-table">
            <thead><tr><th>BJ</th><th>Code</th><th className="num">Amount</th><th>5% RV from</th></tr></thead>
            <tbody>{reserves.map(r => (
              <tr key={r.code}><td>{r.detail?.boekjaar}</td><td>{r.code}</td><td className="num">{fmtEur(r.values?.accounting?.value_eur)}</td><td>{r.detail?.eligible_5pct_from || '—'}</td></tr>
            ))}</tbody>
          </table>
        </DetailModal>
      );
    }

    if (openDetail === 'tax') {
      const reserves = data.positions.filter(p => p.category === 'equity_reserve');
      const overgedragenWinst = data.positions.find(p => p.code === '140000')?.values?.accounting?.value_eur || 0;
      const calc = computeTaxCost(reserves);
      return (
        <DetailModal title="Cost of cash-out — Belgian RV (Roerende Voorheffing)" onClose={() => setOpenDetail(null)}>
          <p style={{ fontSize: 13, color: '#6b7280' }}>
            Distributing equity from a Belgian BV to a private person triggers withholding tax.
            Liquidatiereserves get a reduced 5% rate <strong>after holding for 5 years</strong>.
            Overgedragen winst follows the standard 30% rate (or 15% under VVPR-bis if conditions are met).
          </p>
          <div className="ent-tax-row ent-tax-header">
            <span>Component</span><span className="num">Amount</span><span>If now</span><span>If wait</span>
          </div>
          {calc.map((c, i) => (
            <div key={i} className="ent-tax-row">
              <span>{c.label}</span>
              <span className="num">{fmtEur(c.value)}</span>
              <span>
                <strong>{c.cost_now_pct}%</strong> = {fmtEur(c.cost_now_eur)}
                {c.eligibleNow && <div className="ent-tax-eligible">eligible now ✅</div>}
              </span>
              <span>
                {c.eligibleNow ? '—' : (
                  <>
                    <strong>5%</strong> from {c.eligibleFromIso}
                    <div className="ent-tax-wait">save {fmtEur(c.savings_if_wait)}</div>
                  </>
                )}
              </span>
            </div>
          ))}
          <div className="ent-tax-row" style={{ borderTop: '2px solid #e5e7eb', marginTop: 8 }}>
            <span><strong>Overgedragen winst (140000)</strong></span>
            <span className="num">{fmtEur(overgedragenWinst)}</span>
            <span><strong>30%</strong> = {fmtEur(overgedragenWinst * 0.30)}<div style={{ fontSize: 12, color: '#6b7280' }}>15% with VVPR-bis</div></span>
            <span style={{ fontSize: 12, color: '#6b7280' }}>—</span>
          </div>
        </DetailModal>
      );
    }

    return null;
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-content ent-popup-content" onClick={e => e.stopPropagation()}>
        <div>Loading {slug}…</div>
      </div>
    </div>
  );
  if (error) return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-content ent-popup-content" onClick={e => e.stopPropagation()}>
        <div className="ent-popup-header"><h2>Error</h2><button className="ent-close-btn" onClick={onClose}>×</button></div>
        <div style={{ color: '#b91c1c' }}>{error}</div>
      </div>
    </div>
  );

  const totals = data?.totals || {};
  const equityValue = totals.equity_eur ?? totals.assets_minus_liabilities ?? 0;

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-content ent-popup-content" onClick={e => e.stopPropagation()}>
        <div className="ent-popup-header">
          <h2>{meta?.name || slug} — Eigen vermogen</h2>
          <div className="ent-controls">
            <div className="ent-view-toggle">
              <button className={view === 'accounting' ? 'active' : ''} onClick={() => setView('accounting')}>Account.</button>
              <button className={view === 'economic'   ? 'active' : ''} onClick={() => setView('economic')}>Econ.</button>
              <button disabled title="Enabled when Yuki sync is live (Phase A2)">P&amp;L</button>
            </div>
            <select
              className="ent-date-picker"
              value={snapshotDate || ''}
              onChange={e => setSnapDate(e.target.value || null)}
            >
              <option value="">today (live)</option>
              {snapshots.map(s => (
                <option key={s._id} value={new Date(s.snapshot_date).toISOString()}>
                  {fmtDate(s.snapshot_date)} ({s.source})
                </option>
              ))}
            </select>
            <SeeFilesButton url={getDriveFolderUrl(slug)} />
            <button className="ent-close-btn" onClick={onClose}>×</button>
          </div>
        </div>

        <div className="ent-headline">
          <div className="ent-headline-value">{fmtEur(equityValue)}</div>
          <div className="ent-headline-sub">
            total equity ({view === 'economic' ? 'Economic' : 'Accounting'} view) — assets {fmtEur(totals.assets_eur)} − liabilities {fmtEur(totals.liabilities_eur)}
          </div>
        </div>

        <div className="ent-bs-grid">
          {renderColumn(grouped.assets,      totals.assets_eur,      'ASSETS')}
          {renderColumn(grouped.liabilities, totals.liabilities_eur, 'LIABILITIES')}
        </div>

        {data?.totals?.future_commitments_eur > 0 && (
          <div style={{
            margin: '12px 0',
            padding: '10px 14px',
            background: '#fffbeb',
            border: '1px solid #fde68a',
            borderRadius: 6,
            fontSize: 13,
            color: '#78350f',
            display: 'flex',
            justifyContent: 'space-between',
          }}>
            <span><strong>Future capital commitments</strong> — Maxus I + II unfunded (informational only, not in liabilities)</span>
            <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmtEur(data.totals.future_commitments_eur)}</span>
          </div>
        )}

        <div className="ent-equity-box">
          <h3>
            <span>EIGEN VERMOGEN BREAKDOWN — {fmtEur(equityValue)}</span>
            <span className="ent-tax-link" onClick={() => setOpenDetail('tax')}>cost of cash-out 💰</span>
          </h3>
          {/* Group equity_reserve into one click-through line */}
          {(() => {
            const reserves = grouped.equity.filter(p => p.category === 'equity_reserve');
            const reserveTotal = reserves.reduce((s, p) => s + (p.values?.accounting?.value_eur || 0), 0);
            const others = grouped.equity.filter(p => p.category !== 'equity_reserve');
            return (
              <>
                {reserves.length > 0 && (
                  <div
                    className="ent-row ent-clickable"
                    onClick={() => setOpenDetail('liq')}
                  >
                    <span className="ent-row-label">Liquidatiereserves 2020–2024 ({reserves.length})</span>
                    <span className="ent-row-value">{fmtEur(reserveTotal)}</span>
                    <span className="ent-row-chevron">▸</span>
                  </div>
                )}
                {others.map(renderRow)}
              </>
            );
          })()}
        </div>

        <div className="ent-actions">
          <button className="ent-primary" onClick={() => alert('Edit by double-clicking any value. Each save creates a snapshot.')}>How to edit</button>
          <button onClick={() => alert('In Phase A2 you\'ll be able to upload a fresh ING CSV here.')}>Upload ING extract</button>
          <button onClick={() => alert('In Phase A2 you\'ll be able to log Maxus capital calls / distributions here.')}>+ Capital call / distribution</button>
        </div>

        {detailContent()}
      </div>
    </div>
  );
}

export default EntityPopup;
